import React, { useCallback } from 'react';
import cl from 'classnames';
import isEmpty from 'lodash/isEmpty';

import { SelectedLifestylesSidebarFooterLifestylesSet } from './SelectedLifestylesSidebarFooter.types';

import {
  LifestylesSetSelectedLifestylesID,
  UpdateLifestylesSetCacheAction
} from '../../../../../../lifestylesSets/lifestylesSetsTypes';
import { LifestylesListOnLifestylesSelect } from '../../../../../../lifestyles/components/lists/LifestylesList';

import { useCurrentUser } from '../../../../../../../auth/hooks/useAuth';

import { DownloadLifestylesSetButton } from '../../../../../../lifestylesSets/components/buttons/DownloadLifestylesSetButton';
import { DownloadLifestylesSetSeparatelyButton } from '../../../../../../lifestylesSets/components/buttons/DownloadLifestylesSetSeparatelyButton';
import { SelectedLifestylesSidebarActionsPopover } from '../SelectedLifestylesSidebarActionsPopover';
import { SelectedLifestylesSidebarAttachModalButton } from '../SelectedLifestylesSidebarAttachModalButton';

import { CheckPermissions } from '../../../../../../../helpers/CheckPermissions';
import { PureButtonHelper } from '../../../../../../../helpers/buttons/PureButtonHelper';

import { LifestylesSetsPermissions } from '../../../../../../lifestylesSets/lifestylesSetsConstants';

import { words } from '../../../../../../../locales/keys';

const selectedLifestylesSidebarActionsPermissions = [
  LifestylesSetsPermissions.READ_LIFESTYLES_SET_CLEAR_BUTTON
];

interface SelectedLifestylesSidebarFooterProps {
  lifestylesSet: SelectedLifestylesSidebarFooterLifestylesSet;
  checkedSelectedLifestyleIds: LifestylesSetSelectedLifestylesID[];
  onCustomAttach?: LifestylesListOnLifestylesSelect;
  disabledAttach?: boolean;
  onClose: () => void;
  updateLifestylesSetCache: UpdateLifestylesSetCacheAction<SelectedLifestylesSidebarFooterLifestylesSet>;
}

function SelectedLifestylesSidebarFooter({
  lifestylesSet,
  checkedSelectedLifestyleIds,
  onCustomAttach,
  disabledAttach,
  onClose,
  updateLifestylesSetCache
}: SelectedLifestylesSidebarFooterProps) {
  const currentUser = useCurrentUser();

  const handleCustomAttach = useCallback(() => {
    if (onCustomAttach && lifestylesSet) {
      const checkedLifestyles = lifestylesSet.selectedLifestyles?.filter(
        (lifestyle) => checkedSelectedLifestyleIds?.includes(lifestyle.id)
      );

      onCustomAttach(checkedLifestyles);
    }
  }, [onCustomAttach, lifestylesSet, checkedSelectedLifestyleIds]);

  const withActionsPopover = selectedLifestylesSidebarActionsPermissions.some(
    (action) => currentUser.hasPermissions(action)
  );

  return (
    <div className="justify-between flex space-x-2">
      <CheckPermissions
        action={LifestylesSetsPermissions.READ_LIFESTYLES_SET_DOWNLOAD_BUTTON}
      >
        <DownloadLifestylesSetButton
          lifestylesSetUuid={lifestylesSet.uuid}
          className="font-medium hover:bg-gray-200 items-center px-4 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
        />
      </CheckPermissions>

      <CheckPermissions
        action={
          LifestylesSetsPermissions.READ_LIFESTYLES_SET_DOWNLOAD_SEPARATELY_BUTTON
        }
      >
        <DownloadLifestylesSetSeparatelyButton
          className="font-medium hover:bg-gray-200 items-center px-4 py-2 rounded-md text-gray-700 text-sm dark:hover:bg-gray-700 dark:text-gray-300 focus:ring-offset-0"
          dataGa="download-lifestyles-set-separately-button"
          i18nText={words.download}
          lifestylesSetUuid={lifestylesSet.uuid}
        />
      </CheckPermissions>

      <span className="grow relative z-0 inline-flex justify-end shadow-sm rounded-md">
        {onCustomAttach ? (
          <PureButtonHelper
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            dataGa="selected-lifestyles-set-attach-button"
            i18nText={words.attach}
            onClick={handleCustomAttach}
            disabled={isEmpty(checkedSelectedLifestyleIds) || disabledAttach}
          />
        ) : (
          <SelectedLifestylesSidebarAttachModalButton
            selectedLifestyleIds={checkedSelectedLifestyleIds}
            optimisticUpdate={lifestylesSet.optimisticUpdate}
            onClose={onClose}
            updateLifestylesSetCache={updateLifestylesSetCache}
            className={cl(
              'relative inline-flex items-center px-4 py-2 rounded-l-md bg-blue-500 text-sm font-medium text-white hover:bg-blue-600 focus:z-10',
              withActionsPopover ? 'rounded-l-md' : 'rounded-md'
            )}
            disabled={isEmpty(checkedSelectedLifestyleIds) || disabledAttach}
          />
        )}

        {withActionsPopover && (
          <SelectedLifestylesSidebarActionsPopover
            lifestylesSet={lifestylesSet}
            onClose={onClose}
            updateLifestylesSetCache={updateLifestylesSetCache}
          />
        )}
      </span>
    </div>
  );
}

export default SelectedLifestylesSidebarFooter;
